import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { Outlet, ScrollRestoration, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Toaster } from "@/components/ui/Feedback";
import { Icon } from "@/components/ui/Icon";
import { useScrolled } from "@/hooks";
import { env } from "@/lib/env";

/** Floating scroll-to-top, revealed once the user is well down the page. */
function ToTop() {
  const visible = useScrolled(640);

  return (
    <AnimatePresence>
      {visible ? (
        <motion.button
          type="button"
          className="to-top is-visible"
          aria-label="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          initial={{ opacity: 0, y: 12, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.9 }}
          whileHover={{ scale: 1.07 }}
          whileTap={{ scale: 0.93 }}
          transition={{ duration: 0.22 }}
        >
          <Icon name="arrow-up" />
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
}

/** Visible only while the app is serving bundled data instead of the API. */
function MockBanner() {
  if (!env.useMocks || !env.isDev) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 16,
        bottom: 16,
        zIndex: 140,
        padding: "7px 13px",
        borderRadius: "var(--radius-pill)",
        background: "var(--surface-glass-strong)",
        backdropFilter: "blur(14px)",
        border: "1px solid var(--border-glass)",
        boxShadow: "var(--shadow-soft)",
        fontSize: ".72rem",
        fontWeight: 600,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: "var(--text-muted)",
      }}
      title="Set VITE_USE_MOCKS=false once the FastAPI service is running"
    >
      Mock data
    </div>
  );
}

export function Layout() {
  const location = useLocation();

  // Move focus to the document start on route change so keyboard and screen
  // reader users are not left deep in the previous page.
  useEffect(() => {
    const main = document.getElementById("main");
    main?.focus?.();
  }, [location.pathname]);

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <Header />

      <AnimatePresence mode="wait" initial={false}>
        <Outlet key={location.pathname} />
      </AnimatePresence>

      <Footer />
      <ToTop />
      <Toaster />
      <MockBanner />
      <ScrollRestoration />
    </>
  );
}
